import { format, subDays, subHours, subWeeks } from 'date-fns';
import { api_auth, api_host } from '../common/globals/constants';
import { getApiKeys } from '../utils/fns/get-api-keys';
import { useQuery } from '@tanstack/react-query';

/**
 * Calcula los tiempos de disponibilidad para un periodo de tiempo.
 * @param {string} time - El tipo de periodo ('h', 'd', 'w', '3h', '3d', 'c')
 * @param {Object} statePeriod - Periodo personalizado con startDate y endDate
 * @returns {Object} - Objeto con fechaInicio y fechaTermino
 */
function getDateRange(time, statePeriod) {
  const now = new Date();
  let fechaInicio;
  let fechaTermino = format(now, 'yyyy-MM-dd HH:mm');

  if (time === 'c' && statePeriod) {
    fechaInicio =
      format(new Date(statePeriod.startDate), 'yyyy-MM-dd') + ' 00:00';
    fechaTermino =
      format(new Date(statePeriod.endDate), 'yyyy-MM-dd') + ' 00:00';
    if (fechaInicio === fechaTermino) {
      fechaTermino = format(
        subDays(new Date(fechaInicio), -1),
        'yyyy-MM-dd HH:mm',
      );
    }
  } else if (time === 'd') {
    fechaInicio = format(subDays(now, 1), 'yyyy-MM-dd HH:mm');
  } else if (time === 'w') {
    fechaInicio = format(subWeeks(now, 1), 'yyyy-MM-dd HH:mm');
  } else if (time === '3h') {
    fechaInicio = format(subHours(now, 3), 'yyyy-MM-dd HH:mm');
  } else if (time === '3d') {
    fechaInicio = format(subDays(now, 3), 'yyyy-MM-dd HH:mm');
  } else {
    // Por defecto última hora
    fechaInicio = format(subHours(now, 1), 'yyyy-MM-dd HH:mm');
  }

  return { fechaInicio, fechaTermino };
}

/**
 * Hook para obtener datos de eventos por pasos desde la API.
 * Solo se encarga de realizar el fetch, el procesamiento se hace en el componente.
 *
 * @param {Object} params - Parámetros de la consulta
 * @param {number} params.objetivoId - ID del objetivo
 * @param {string} params.time - Tipo de periodo ('h', 'd', 'w', '3h', '3d', 'c')
 * @param {Object} params.statePeriod - Periodo personalizado
 * @param {boolean} params.enabled - Habilitar la consulta
 * @param {number|boolean} params.refreshTime - Intervalo de refresco
 * @returns {Object} - Resultado de la query con datos crudos de la API
 */
export function useDisponibilidadPorPasos({
  objetivoId,
  time = 'h',
  statePeriod = null,
  enabled = true,
  refreshTime = false,
}) {
  const { fechaInicio, fechaTermino } = getDateRange(time, statePeriod);

  return useQuery({
    queryKey: [
      'disponibilidad-por-pasos',
      objetivoId,
      fechaInicio,
      fechaTermino,
      time,
    ],
    queryFn: async () => {
      const apiKeys = getApiKeys();
      const token = apiKeys.token;
      const userId = apiKeys.user;

      const requestUrl = `${api_host}/reporte/online/eventos/Reporte3Events/?user=${userId}&key=${api_auth}&objetive=${objetivoId}&datebegin=${fechaInicio}&finaldate=${fechaTermino}&hash=3276bd408a114308ab00c6133fe29fb2&token=${token}`;

      const response = await fetch(requestUrl);
      if (!response.ok) {
        throw new Error('Error al obtener los eventos');
      }
      const eventsData = await response.json();

      if (eventsData.status === 1 || eventsData.status === 5) {
        throw new Error('No autenticado');
      }

      return eventsData;
    },
    enabled: enabled && !!objetivoId,
    gcTime: 0,
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
    refetchInterval: refreshTime || false,
  });
}
